import React, { useEffect, useRef, useState } from 'react';

/**
 * MobileCarousel
 * - Renders children as horizontally snapping cards.
 * - Auto-advances every `interval` ms (default 2500).
 * - Pauses on user swipe/touch and resumes 4s after interaction.
 * - Designed for mobile only; parent should wrap in `md:hidden`.
 */
export const MobileCarousel = ({
  children,
  interval = 2500,
  slideWidthClass = 'w-[82%]',
  testId,
}) => {
  const items = React.Children.toArray(children);
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const resumeTimeoutRef = useRef(null);

  // Auto-advance
  useEffect(() => {
    if (isPaused || items.length <= 1) return;

    const timer = setInterval(() => {
      const container = containerRef.current;
      if (!container) return;

      const next = (activeIndex + 1) % items.length;
      const slide = container.children[next];
      if (slide) {
        container.scrollTo({
          left: slide.offsetLeft - container.offsetLeft,
          behavior: 'smooth',
        });
      }
      setActiveIndex(next);
    }, interval);

    return () => clearInterval(timer);
  }, [activeIndex, isPaused, items.length, interval]);

  // Track manual scroll to update active dot
  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const slideWidth = container.children[0]?.offsetWidth || 1;
    const idx = Math.round(container.scrollLeft / slideWidth);
    if (idx !== activeIndex && idx >= 0 && idx < items.length) {
      setActiveIndex(idx);
    }
  };

  const pauseAutoplay = () => {
    setIsPaused(true);
    if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(() => setIsPaused(false), 4000);
  };

  useEffect(() => {
    return () => {
      if (resumeTimeoutRef.current) clearTimeout(resumeTimeoutRef.current);
    };
  }, []);

  const goTo = (idx) => {
    const container = containerRef.current;
    if (!container) return;
    const slide = container.children[idx];
    if (slide) {
      container.scrollTo({
        left: slide.offsetLeft - container.offsetLeft,
        behavior: 'smooth',
      });
    }
    pauseAutoplay();
  };

  return (
    <div className="relative" data-testid={testId}>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        onTouchStart={pauseAutoplay}
        onMouseDown={pauseAutoplay}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory scroll-smooth scrollbar-hide pb-2 -mx-4 px-4"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {items.map((child, idx) => (
          <div
            key={idx}
            className={`${slideWidthClass} flex-shrink-0 snap-center`}
          >
            {child}
          </div>
        ))}
      </div>

      {/* Dots */}
      {items.length > 1 && (
        <div className="flex justify-center gap-2 mt-5">
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              aria-label={`Go to slide ${idx + 1}`}
              onClick={() => goTo(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === activeIndex
                  ? 'w-6 bg-blue-600'
                  : 'w-2 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MobileCarousel;
